import { CodeBlock, Alert, Table, Step, Badge, CardGrid, Card } from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function TestingGuide() {
	return (
		<div className={styles.docsPage}>
			<h1>Testing Guide</h1>
			<p className={styles.lead}>
				This guide covers everything you need to test your Ringfort Studio integration, from
				setting up your sandbox environment to load testing and going live.
			</p>

			<h2>Setting Up Sandbox Environment</h2>
			<p>
				Before integrating with production, use our sandbox environment to test all functionality
				without risking real funds. The sandbox environment is a complete replica of production
				with simulated wallet operations.
			</p>

			<h3>Sandbox vs Production</h3>
			<Table
				columns={[
					{ key: 'feature', header: 'Feature' },
					{ key: 'sandbox', header: 'Sandbox' },
					{ key: 'production', header: 'Production' },
				]}
				data={[
					{ feature: 'Base URL', sandbox: 'https://sandbox-api.ringfort.studio', production: 'https://api.ringfort.studio' },
					{ feature: 'Real Funds', sandbox: 'No (simulated)', production: 'Yes' },
					{ feature: 'Rate Limits', sandbox: '100 req/min', production: '1000 req/min' },
					{ feature: 'Webhooks', sandbox: 'Full support', production: 'Full support' },
					{ feature: 'Game RNG', sandbox: 'Provably fair', production: 'Provably fair' },
					{ feature: 'Data Retention', sandbox: '30 days', production: 'Unlimited' },
				]}
			/>

			<h3>Getting Sandbox Credentials</h3>
			<Step number={1} title="Create Operator Account">
				<p>
					Sign up for an operator account at <code>dashboard.ringfort.studio</code>.
					You will receive both sandbox and production API keys upon approval.
				</p>
			</Step>

			<Step number={2} title="Locate API Keys">
				<p>
					Navigate to Settings &gt; API Keys in your operator dashboard. You will find two sets of credentials:
				</p>
				<ul>
					<li><strong>Sandbox API Key:</strong> Prefix <code>sk_sandbox_</code></li>
					<li><strong>Production API Key:</strong> Prefix <code>sk_live_</code></li>
				</ul>
			</Step>

			<Step number={3} title="Configure Webhook URLs">
				<p>
					Set up separate webhook endpoints for sandbox testing. This allows you to test
					webhook handling without affecting your production systems.
				</p>
			</Step>

			<h2>Test Credentials and Accounts</h2>
			<p>
				Use these test credentials and special player IDs to simulate various scenarios in the sandbox environment.
			</p>

			<h3>Test API Keys</h3>
			<CodeBlock language="bash" title="Sandbox API Key Format">
{`# Your sandbox API key will look like:
sk_sandbox_abc123def456...

# Use it in the Authorization header:
Authorization: Bearer sk_sandbox_abc123def456...`}
			</CodeBlock>

			<h3>Special Test Player IDs</h3>
			<p>
				These special player IDs trigger specific behaviors in the sandbox for testing edge cases:
			</p>
			<Table
				columns={[
					{ key: 'playerId', header: 'Player ID' },
					{ key: 'behavior', header: 'Behavior' },
					{ key: 'useCase', header: 'Use Case' },
				]}
				data={[
					{ playerId: <code>test_player_normal</code>, behavior: 'Standard behavior', useCase: 'Normal gameplay testing' },
					{ playerId: <code>test_player_winner</code>, behavior: 'Always wins (when possible)', useCase: 'Testing win payouts' },
					{ playerId: <code>test_player_loser</code>, behavior: 'Always loses', useCase: 'Testing loss scenarios' },
					{ playerId: <code>test_player_slow</code>, behavior: '2s response delay', useCase: 'Testing timeout handling' },
					{ playerId: <code>test_player_error</code>, behavior: 'Triggers wallet errors', useCase: 'Error handling testing' },
					{ playerId: <code>test_player_low_balance</code>, behavior: 'Balance always 0.01', useCase: 'Insufficient funds testing' },
				]}
			/>

			<Alert variant="info" title="Test Player Limits">
				Test player IDs only work in sandbox mode. Using them in production will result in
				normal player behavior.
			</Alert>

			<h2>Testing Session Creation</h2>
			<p>
				Sessions are the foundation of player interactions. Test session creation thoroughly
				to ensure players can always access games.
			</p>

			<h3>Create a Test Session</h3>
			<CodeBlock language="bash" title="Create Session Request">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/sessions \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "player_id": "test_player_normal",
    "player_nickname": "TestPlayer",
    "currency": "USD",
    "locale": "en-US",
    "metadata": {
      "test_scenario": "session_creation"
    }
  }'`}
			</CodeBlock>

			<h3>Expected Response</h3>
			<CodeBlock language="json" title="Successful Session Response">
{`{
  "session_id": "ses_abc123def456",
  "game_url": "https://games.ringfort.studio/play?token=eyJ...",
  "expires_at": "2024-01-22T12:00:00Z",
  "player": {
    "id": "test_player_normal",
    "nickname": "TestPlayer",
    "currency": "USD"
  }
}`}
			</CodeBlock>

			<h3>Session Testing Checklist</h3>
			<ul>
				<li>Verify the <code>game_url</code> loads correctly in an iframe</li>
				<li>Test session expiration handling (default 24 hours)</li>
				<li>Verify metadata is correctly passed through</li>
				<li>Test with different locales (en-US, es-ES, pt-BR, etc.)</li>
				<li>Confirm currency formatting matches player locale</li>
			</ul>

			<h2>Testing Each Game Type</h2>
			<p>
				Each game has unique mechanics. Test all games your players will access.
			</p>

			<h3>Dice Game Testing</h3>
			<CodeBlock language="bash" title="Place Dice Bet">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/games/dice/bet \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "10.00",
    "target": 50,
    "condition": "under"
  }'`}
			</CodeBlock>

			<p><strong>Key test scenarios for Dice:</strong></p>
			<ul>
				<li>Test target values at boundaries (1, 98)</li>
				<li>Test both "over" and "under" conditions</li>
				<li>Verify multiplier calculations match expected values</li>
				<li>Test minimum and maximum bet amounts</li>
			</ul>

			<h3>Plinko Game Testing</h3>
			<CodeBlock language="bash" title="Place Plinko Bet">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/games/plinko/bet \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "5.00",
    "rows": 12,
    "risk_level": "medium"
  }'`}
			</CodeBlock>

			<p><strong>Key test scenarios for Plinko:</strong></p>
			<ul>
				<li>Test all row configurations (8, 10, 12, 14, 16)</li>
				<li>Test all risk levels (low, medium, high)</li>
				<li>Verify bucket multipliers match documentation</li>
				<li>Test path animation consistency</li>
			</ul>

			<h3>Coin Flip Game Testing</h3>
			<CodeBlock language="bash" title="Place Coin Flip Bet">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/games/coinflip/bet \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "20.00",
    "prediction": "heads"
  }'`}
			</CodeBlock>

			<p><strong>Key test scenarios for Coin Flip:</strong></p>
			<ul>
				<li>Test both "heads" and "tails" predictions</li>
				<li>Verify 1.98x multiplier (1% house edge)</li>
				<li>Test rapid successive bets</li>
			</ul>

			<h3>Crash Game Testing</h3>
			<CodeBlock language="bash" title="Place Crash Bet">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/games/crash/bet \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "15.00",
    "auto_cashout": 2.00
  }'`}
			</CodeBlock>

			<p><strong>Key test scenarios for Crash:</strong></p>
			<ul>
				<li>Test manual cashout at various multipliers</li>
				<li>Test auto-cashout functionality</li>
				<li>Verify crash point fairness over multiple rounds</li>
				<li>Test joining mid-round (should be rejected)</li>
			</ul>

			<h2>Testing Wallet Operations</h2>
			<p>
				Wallet operations are critical for player funds management. Test all wallet modes you plan to use.
			</p>

			<h3>Seamless Wallet Mode (Recommended)</h3>
			<p>
				In seamless mode, Ringfort calls your webhook for every transaction. Your server
				maintains the source of truth for player balances.
			</p>

			<h4>Testing Debit (Bet Placement)</h4>
			<p>When a player places a bet, Ringfort sends a debit request to your webhook:</p>
			<CodeBlock language="json" title="Incoming Debit Webhook">
{`POST https://your-server.com/webhooks/ringfort

{
  "event": "wallet.debit",
  "transaction_id": "txn_bet_abc123",
  "player_id": "player_123",
  "amount": "10.00",
  "currency": "USD",
  "reference": "bet_dice_xyz789",
  "metadata": {
    "game": "dice",
    "bet_id": "bet_xyz789"
  }
}`}
			</CodeBlock>

			<h4>Expected Response for Success</h4>
			<CodeBlock language="json" title="Debit Success Response">
{`{
  "status": "success",
  "transaction_id": "txn_bet_abc123",
  "balance": "990.00"
}`}
			</CodeBlock>

			<h4>Expected Response for Insufficient Funds</h4>
			<CodeBlock language="json" title="Insufficient Funds Response">
{`{
  "status": "error",
  "error_code": "INSUFFICIENT_FUNDS",
  "message": "Player balance is insufficient",
  "balance": "5.00"
}`}
			</CodeBlock>

			<h4>Testing Credit (Win Payout)</h4>
			<p>When a player wins, Ringfort sends a credit request:</p>
			<CodeBlock language="json" title="Incoming Credit Webhook">
{`POST https://your-server.com/webhooks/ringfort

{
  "event": "wallet.credit",
  "transaction_id": "txn_win_def456",
  "player_id": "player_123",
  "amount": "19.80",
  "currency": "USD",
  "reference": "win_dice_xyz789",
  "metadata": {
    "game": "dice",
    "bet_id": "bet_xyz789",
    "multiplier": "1.98"
  }
}`}
			</CodeBlock>

			<h3>Transfer Wallet Mode</h3>
			<p>
				In transfer mode, players deposit funds into a Ringfort-managed wallet and withdraw when finished.
			</p>

			<h4>Testing Deposits</h4>
			<CodeBlock language="bash" title="Initiate Deposit">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/wallet/deposit \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "100.00",
    "reference": "deposit_from_operator"
  }'`}
			</CodeBlock>

			<h4>Testing Withdrawals</h4>
			<CodeBlock language="bash" title="Initiate Withdrawal">
{`curl -X POST https://sandbox-api.ringfort.studio/v1/wallet/withdraw \\
  -H "Authorization: Bearer sk_sandbox_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "ses_abc123def456",
    "amount": "50.00",
    "reference": "withdrawal_to_operator"
  }'`}
			</CodeBlock>

			<h2>Testing Webhooks</h2>
			<p>
				Webhooks are essential for real-time event notifications. Test all webhook types you subscribe to.
			</p>

			<h3>Using webhook.site for Testing</h3>
			<Step number={1} title="Create Webhook Endpoint">
				<p>
					Visit <a href="https://webhook.site" target="_blank" rel="noopener noreferrer">webhook.site</a> and
					copy your unique URL (e.g., <code>https://webhook.site/abc123-def456</code>).
				</p>
			</Step>

			<Step number={2} title="Configure in Dashboard">
				<p>
					Go to your Ringfort operator dashboard, navigate to Settings &gt; Webhooks, and add your
					webhook.site URL as a sandbox webhook endpoint.
				</p>
			</Step>

			<Step number={3} title="Trigger Test Events">
				<p>
					Use the "Send Test Event" button in the dashboard or place test bets to trigger real webhook deliveries.
				</p>
			</Step>

			<h3>Webhook Event Types</h3>
			<Table
				columns={[
					{ key: 'event', header: 'Event' },
					{ key: 'description', header: 'Description' },
					{ key: 'required', header: 'Required' },
				]}
				data={[
					{ event: <code>wallet.debit</code>, description: 'Player bet placement', required: <Badge variant="required" /> },
					{ event: <code>wallet.credit</code>, description: 'Player win payout', required: <Badge variant="required" /> },
					{ event: <code>session.created</code>, description: 'New session started', required: <Badge variant="optional" /> },
					{ event: <code>session.ended</code>, description: 'Session terminated', required: <Badge variant="optional" /> },
					{ event: <code>bet.placed</code>, description: 'Bet was placed', required: <Badge variant="optional" /> },
					{ event: <code>bet.settled</code>, description: 'Bet result determined', required: <Badge variant="optional" /> },
				]}
			/>

			<h3>Verifying Webhook Signatures</h3>
			<p>
				All webhooks include an HMAC-SHA256 signature for verification. Always validate signatures
				in production to prevent spoofed requests.
			</p>
			<CodeBlock language="typescript" title="Webhook Signature Verification">
{`import crypto from 'crypto';

function verifyWebhookSignature(
  payload: string,
  signature: string,
  secret: string
): boolean {
  const expectedSignature = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');

  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expectedSignature)
  );
}

// Usage in your webhook handler
app.post('/webhooks/ringfort', (req, res) => {
  const signature = req.headers['x-ringfort-signature'];
  const payload = JSON.stringify(req.body);

  if (!verifyWebhookSignature(payload, signature, WEBHOOK_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  // Process the webhook...
});`}
			</CodeBlock>

			<Alert variant="warning" title="Webhook Retry Policy">
				If your endpoint returns a non-2xx status code, Ringfort will retry the webhook up to
				5 times with exponential backoff (1s, 2s, 4s, 8s, 16s). Ensure your handler is idempotent
				to handle duplicate deliveries.
			</Alert>

			<h2>Automated Testing Recommendations</h2>
			<p>
				Build a comprehensive test suite to validate your integration automatically.
			</p>

			<h3>Recommended Test Structure</h3>
			<CodeBlock language="typescript" title="Integration Test Example">
{`import { describe, it, expect, beforeAll, afterAll } from 'vitest';
import { RingfortClient } from './ringfort-client';

describe('Ringfort Integration Tests', () => {
  let client: RingfortClient;
  let sessionId: string;

  beforeAll(async () => {
    client = new RingfortClient({
      apiKey: process.env.RINGFORT_SANDBOX_KEY,
      baseUrl: 'https://sandbox-api.ringfort.studio'
    });

    // Create test session
    const session = await client.createSession({
      playerId: 'test_player_normal',
      playerNickname: 'AutoTest',
      currency: 'USD'
    });
    sessionId = session.session_id;
  });

  afterAll(async () => {
    // Cleanup: end session
    await client.endSession(sessionId);
  });

  describe('Dice Game', () => {
    it('should place a valid bet', async () => {
      const result = await client.placeDiceBet({
        sessionId,
        amount: '10.00',
        target: 50,
        condition: 'under'
      });

      expect(result.bet_id).toBeDefined();
      expect(result.roll).toBeGreaterThanOrEqual(0);
      expect(result.roll).toBeLessThanOrEqual(100);
    });

    it('should reject bet with invalid target', async () => {
      await expect(
        client.placeDiceBet({
          sessionId,
          amount: '10.00',
          target: 99,  // Too high
          condition: 'under'
        })
      ).rejects.toThrow('Target must be between 1 and 98');
    });
  });

  describe('Wallet Operations', () => {
    it('should reject bet with insufficient funds', async () => {
      // Use special test player
      const lowBalanceSession = await client.createSession({
        playerId: 'test_player_low_balance',
        currency: 'USD'
      });

      await expect(
        client.placeDiceBet({
          sessionId: lowBalanceSession.session_id,
          amount: '100.00',
          target: 50,
          condition: 'under'
        })
      ).rejects.toThrow('INSUFFICIENT_FUNDS');
    });
  });
});`}
			</CodeBlock>

			<h3>CI/CD Integration</h3>
			<CodeBlock language="bash" title="GitHub Actions Example">
{`# .github/workflows/integration-tests.yml
name: Integration Tests

on:
  push:
    branches: [main, develop]
  pull_request:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Setup Node.js
        uses: actions/setup-node@v4
        with:
          node-version: '20'

      - name: Install dependencies
        run: npm ci

      - name: Run integration tests
        env:
          RINGFORT_SANDBOX_KEY: \${{ secrets.RINGFORT_SANDBOX_KEY }}
          WEBHOOK_SECRET: \${{ secrets.WEBHOOK_SECRET }}
        run: npm run test:integration`}
			</CodeBlock>

			<h2>Load Testing Considerations</h2>
			<p>
				Before going live, validate your integration can handle expected traffic volumes.
			</p>

			<h3>Sandbox Rate Limits</h3>
			<Alert variant="info">
				Sandbox has a rate limit of 100 requests per minute per API key. Contact support if you
				need higher limits for load testing.
			</Alert>

			<h3>Key Metrics to Monitor</h3>
			<Table
				columns={[
					{ key: 'metric', header: 'Metric' },
					{ key: 'target', header: 'Target' },
					{ key: 'critical', header: 'Critical Threshold' },
				]}
				data={[
					{ metric: 'API Response Time (p95)', target: '< 200ms', critical: '> 1000ms' },
					{ metric: 'Webhook Delivery Time', target: '< 500ms', critical: '> 5000ms' },
					{ metric: 'Error Rate', target: '< 0.1%', critical: '> 1%' },
					{ metric: 'Session Creation Success', target: '> 99.9%', critical: '< 99%' },
				]}
			/>

			<h3>Load Testing Script Example</h3>
			<CodeBlock language="typescript" title="k6 Load Test Script">
{`import http from 'k6/http';
import { check, sleep } from 'k6';

export const options = {
  stages: [
    { duration: '1m', target: 10 },   // Ramp up
    { duration: '5m', target: 10 },   // Steady state
    { duration: '1m', target: 50 },   // Spike
    { duration: '2m', target: 50 },   // Sustained spike
    { duration: '1m', target: 0 },    // Ramp down
  ],
  thresholds: {
    http_req_duration: ['p(95)<500'],
    http_req_failed: ['rate<0.01'],
  },
};

const BASE_URL = 'https://sandbox-api.ringfort.studio';
const API_KEY = __ENV.RINGFORT_SANDBOX_KEY;

export default function () {
  // Create session
  const sessionRes = http.post(
    \`\${BASE_URL}/v1/sessions\`,
    JSON.stringify({
      player_id: \`load_test_\${__VU}_\${__ITER}\`,
      currency: 'USD',
    }),
    {
      headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        'Content-Type': 'application/json',
      },
    }
  );

  check(sessionRes, {
    'session created': (r) => r.status === 201,
  });

  const session = JSON.parse(sessionRes.body);

  // Place bet
  const betRes = http.post(
    \`\${BASE_URL}/v1/games/dice/bet\`,
    JSON.stringify({
      session_id: session.session_id,
      amount: '1.00',
      target: 50,
      condition: 'under',
    }),
    {
      headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        'Content-Type': 'application/json',
      },
    }
  );

  check(betRes, {
    'bet placed': (r) => r.status === 200,
  });

  sleep(1);
}`}
			</CodeBlock>

			<h2>Common Issues and Solutions</h2>

			<h3>Authentication Errors</h3>
			<Table
				columns={[
					{ key: 'error', header: 'Error' },
					{ key: 'cause', header: 'Cause' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{ error: <code>401 Unauthorized</code>, cause: 'Invalid or missing API key', solution: 'Verify API key format and ensure correct environment (sandbox vs production)' },
					{ error: <code>403 Forbidden</code>, cause: 'API key lacks permission', solution: 'Check API key scopes in dashboard' },
					{ error: <code>INVALID_SIGNATURE</code>, cause: 'Webhook signature mismatch', solution: 'Verify webhook secret and payload format' },
				]}
			/>

			<h3>Session Errors</h3>
			<Table
				columns={[
					{ key: 'error', header: 'Error' },
					{ key: 'cause', header: 'Cause' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{ error: <code>SESSION_EXPIRED</code>, cause: 'Session older than 24 hours', solution: 'Create a new session for the player' },
					{ error: <code>SESSION_NOT_FOUND</code>, cause: 'Invalid session ID', solution: 'Verify session ID format and existence' },
					{ error: <code>PLAYER_ALREADY_HAS_SESSION</code>, cause: 'Duplicate session attempt', solution: 'End existing session first or reuse current session' },
				]}
			/>

			<h3>Webhook Issues</h3>
			<Table
				columns={[
					{ key: 'issue', header: 'Issue' },
					{ key: 'cause', header: 'Cause' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{ issue: 'Webhooks not received', cause: 'Firewall blocking requests', solution: 'Whitelist Ringfort IP ranges (see docs)' },
					{ issue: 'Duplicate webhooks', cause: 'Slow response causing retry', solution: 'Return 200 quickly, process async' },
					{ issue: 'Signature validation failing', cause: 'Payload modified by proxy', solution: 'Use raw body for signature verification' },
				]}
			/>

			<h2>Pre-Launch Checklist</h2>
			<p>
				Complete this checklist before going live with your integration:
			</p>

			<Alert variant="success" title="Integration Checklist">
				<ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
					<li><strong>Authentication:</strong> API keys rotate correctly, stored securely</li>
					<li><strong>Sessions:</strong> Creation, expiration, and cleanup work properly</li>
					<li><strong>All Games:</strong> Each game type tested with various parameters</li>
					<li><strong>Wallet:</strong> Deposits, bets, wins, and withdrawals process correctly</li>
					<li><strong>Webhooks:</strong> All event types handled, signatures verified</li>
					<li><strong>Error Handling:</strong> All error codes handled gracefully</li>
					<li><strong>Provably Fair:</strong> Verification endpoints tested and working</li>
					<li><strong>Load Testing:</strong> System handles expected peak traffic</li>
					<li><strong>Monitoring:</strong> Alerts configured for errors and latency</li>
					<li><strong>Logging:</strong> All transactions logged for audit purposes</li>
				</ul>
			</Alert>

			<h3>Production Readiness Review</h3>
			<p>
				Before switching to production, schedule a readiness review with your Ringfort account
				manager. We will:
			</p>
			<ul>
				<li>Review your integration architecture</li>
				<li>Validate webhook handling implementation</li>
				<li>Confirm compliance requirements are met</li>
				<li>Set up production monitoring alerts</li>
				<li>Provide production API keys</li>
			</ul>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={2}>
					<Card
						title="Provably Fair"
						description="Learn how our provably fair system works and how to verify game outcomes."
						href="/docs/provably-fair"
					/>
					<Card
						title="Error Codes"
						description="Complete reference of all error codes and how to handle them."
						href="/docs/errors"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
